import { FC, useEffect, useState, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Stack,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { Document, Page, Text, View, Image, Font } from '@react-pdf/renderer';
import { PdfPreviewWithTools } from '../../../PdfPreviewWithTools';
import {
  buildFactsheetDownloadFileName,
  resolveFactsheetDownloadAsOf,
} from '../../../../../../utils/documentFileName';
import { pdfStyles } from './styles';
import http from '../../../../../../apis/http';
import moment from 'moment';
import 'moment/locale/de';
import { SectionHeaders, SECTION_HEADER_DEFAULTS } from '../PdfConfig';
import { resolveCommentaryFirstPartWords, splitCommentary } from '../commentarySplit';
import { client } from '../../../../../../App';
import { GET_AUTOMATED_COLUMNS } from '../../../../../../queries/widgets';

const dialogStyles = {
  dialog: {
    '& .MuiDialog-paper': {
      width: '90vw',
      height: '90vh',
      maxWidth: '1200px',
      maxHeight: '900px',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    },
  },
  dialogTitle: {
    borderBottom: '1px solid #e5e7eb',
    padding: '16px 24px',
    '& .MuiTypography-root': {
      fontSize: '18px',
      fontWeight: 600,
    },
  },
  dialogContent: {
    padding: '0 !important',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  pdfContainer: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    background: '#525659',
  },
};

interface PdfPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  data: any;
  template: any;
  title?: string;
  pieChartImages?: { countryChartUrl?: string; sectorChartUrl?: string };
  isin?: string; // ISIN for GraphQL queries
}

// PDF Document Component
export const FactsheetPDF: FC<{ data: any, logoSrc?: string, chart1ImageUrl?: string, chart2ImageUrl?: string, chart3ImageUrl?: string, pieChart1ImageUrl?: string, pieChart2ImageUrl?: string, pieChart3ImageUrl?: string, bannerImageSrc?: string, manImageSrc?: string, chartImageSrc?: string, awardsImageSrc?: string, isin?: string, cellDataCache?: Map<string, any>, until?: string }> = ({ data, logoSrc, chart1ImageUrl, chart2ImageUrl, chart3ImageUrl, pieChart1ImageUrl, pieChart2ImageUrl, pieChart3ImageUrl, bannerImageSrc, manImageSrc, chartImageSrc, awardsImageSrc, cellDataCache, until }) => {
  const effectiveData = data || {};
  const performanceNotice: string =
    typeof effectiveData.performanceNotice === 'string' ? effectiveData.performanceNotice.trim() : '';
  // Allocation fallback: when no pie image was captured, print the values as a list instead of a dev placeholder.
  const formatAllocationValue = (value: number) => `${Number(value).toFixed(2).replace('.', ',')} %`;
  const renderAllocationFallback = (items: Array<{ label: string; value: number }> | undefined) =>
    items && items.length > 0 ? (
      <View style={pdfStyles.allocationFallbackList}>
        {items.slice(0, 8).map((item, index) => (
          <View key={index} style={pdfStyles.allocationFallbackRow}>
            <Text style={pdfStyles.allocationFallbackLabel}>{item.label}</Text>
            <Text style={pdfStyles.allocationFallbackValue}>{formatAllocationValue(item.value)}</Text>
          </View>
        ))}
      </View>
    ) : null;
  const resolvedChart1ImageUrl = chart1ImageUrl ?? effectiveData.chart1ImageDataUrl;
  const resolvedChart2ImageUrl = chart2ImageUrl ?? effectiveData.chart2ImageDataUrl;
  const resolvedChart3ImageUrl = chart3ImageUrl ?? effectiveData.chart3ImageDataUrl;
  const resolvedPieChart1ImageUrl = pieChart1ImageUrl ?? effectiveData.pieChart1ImageDataUrl;
  const resolvedPieChart2ImageUrl = pieChart2ImageUrl ?? effectiveData.pieChart2ImageDataUrl;
  const resolvedPieChart3ImageUrl = pieChart3ImageUrl ?? effectiveData.pieChart3ImageDataUrl;
  const effectiveTitle = effectiveData.title || 'Active Mining I';
  const effectiveSubtitle = effectiveData.subtitle || 'Das Anlageuniversum des Fonds sind Rohstoffaktien aus dem Bereich Erzgewinnung und Verhüttung.';
  const effectiveDate = effectiveData.published_at || 'November 2025';
  const formattedDate = moment(effectiveDate, ['YYYY-MM-DD', 'YYYY-MM']).locale('de').format('MMMM YYYY').toUpperCase();
  const sectionHeaders: SectionHeaders = {
    ...SECTION_HEADER_DEFAULTS,
    ...(effectiveData.sectionHeaders || {}),
  };

  // Master Data
  const masterData = effectiveData.masterData || {
    fondskategorie: 'Aktienfonds',
    auflagedatum: '02.09.2024',
    wkn: 'A3EWBE',
    isin: 'DE000A3EWBE8',
    erstausgabepreis: '148,37 EUR',
    fondsvolumen: '9,96 Mio. EUR',
    kursNav: '146,39 EUR',
    fondswaehrung: 'EUR',
    fondsdomizil: 'DE',
    geschaeftsjahresende: '31-Aug.',
    ertragsverwendung: 'Thesaurierend',
    rechtsform: 'OGAW-Sondervermögen',
    risikoErtragsprofil: '4',
    sparplanfaehig: 'ja',
    mindestzeichnungbetrag: '100.000 EUR',
  };

  // Fees
  const fees = effectiveData.fees || {
    ausgabeaufschlag: '0% (MAX 5%)',
    laufendeKostenTer: '1,94%',
    davonVerwaltungsgebuehr: '1,23% (MAX 2%)',
  };

  // Fund Manager
  const fundManager = effectiveData.fundManager || {
    name: 'Kai Heinrich',
    company: 'Plutos Vermögensverwaltung',
    research: 'Research & Strategie: Markus Mezger',
    company2: 'Micro meets Macro GmbH',
    phone: '+49 61 28 95 23 0',
    hedgingRatio: '35%',
    photoUrl: undefined,
  };

  // Monthly commentary — split into two wrap={false} cards so page break keeps
  // border-radius and page-2 top padding (react-pdf clips a single wrapping card).
  const monthlyCommentary =
    typeof effectiveData.monthlyCommentary === 'string' ? effectiveData.monthlyCommentary.trim() : '';
  const commentaryParts = splitCommentary(
    monthlyCommentary,
    resolveCommentaryFirstPartWords(effectiveData.commentaryFirstPartWords)
  );
  const firstPartCommentary = commentaryParts.first;
  const secondPartCommentary = commentaryParts.second;

  // Helper function to get display value (fetched or static)
  const getDisplayValue = (section: string, field: string, defaultValue: string): string => {
    const value = effectiveData[section]?.[field];
    
    // If it's a dynamic value, try to get from cache
    if (typeof value === 'object' && value?.isDynamic && value?.fields?.length) {
      const valueKey = `${section}.${field}-${until || ''}`;
      const fetchedValue = cellDataCache?.get(valueKey);
      if (fetchedValue !== undefined && fetchedValue !== null) {
        return String(fetchedValue);
      }
      // If not in cache yet, return default while loading (ensure it's a string)
      return typeof defaultValue === 'string' ? defaultValue : '';
    }
    
    // Return static value or default (ensure it's a string)
    if (typeof value === 'string') {
      return value;
    }
    return typeof defaultValue === 'string' ? defaultValue : '';
  };

  // Get cumulative performance data - support both old and new structure
  const getCumulativePerformanceData = () => {
    const perf = effectiveData.cumulativePerformance;
    // New V1-style structure (labels/values/columns)
    if (perf && perf.labels && Array.isArray(perf.labels) && perf.values && Array.isArray(perf.values)) {
      return {
        labels: perf.labels,
        values: perf.values,
        columns: perf.columns || [],
      };
    }
    // Old key-value structure - migrate on the fly
    if (perf && typeof perf === 'object' && !perf.labels) {
      return {
        labels: ['1 Monat', '6 Monate', '1 Jahr', '3 Jahre', '5 Jahre', 'Lfd. Jahr', 'seit Auflage'],
        values: [[
          perf.oneMonth || '+1,76',
          perf.sixMonths || '-3,04',
          perf.oneYear || '-2,54',
          perf.threeYears || '-1,07',
          perf.fiveYears || '-0,68',
          perf.currentYear || '+0,95',
          perf.sinceInception || '+95,38'
        ]],
        columns: [],
      };
    }
    // Default
    return {
      labels: ['1 Monat', '6 Monate', '1 Jahr', '3 Jahre', '5 Jahre', 'Lfd. Jahr', 'seit Auflage'],
      values: [['+1,76', '-3,04', '-2,54', '-1,07', '-0,68', '+0,95', '+95,38']],
      columns: [],
    };
  };
  const cumulativePerformanceData = getCumulativePerformanceData();

  // Rolling Performance Data
  const rollingPerformance = effectiveData.rollingPerformance || {
    periods: [
      { label: '08.09.2020 bis 08.09.2021', netto: 28.63, brutto: 35.40 },
      { label: '08.09.2021 bis 08.09.2022', netto: -13.13, brutto: -13.13 },
      { label: '08.09.2022 bis 08.09.2023', netto: 7.59, brutto: 7.59 },
      { label: '08.09.2023 bis 09.09.2024', netto: 7.36, brutto: 7.36 },
      { label: '09.09.2024 bis 09.09.2025', netto: 13.06, brutto: 13.06 },
    ],
  };

  // Investment Strategy
  const investmentStrategy = effectiveData.investmentStrategy || 'Der Active Mining Fund ist ein offensiver Mischfonds mit dem Fokus auf globale Aktien aus dem Bergbau- und Rohstoffsektor. Seit dem 01.01.2019 hat der Fonds ein passives Anlagekonzept mit dem Ziel einer Überinvestition des Fonds mit dividendenstarken Aktien, die sich durch ein positives Momentum auszeichnen. Das Fondsmanagement beachtet bei der Auswahl der Investments fundamentale Kennzahlen, ergänzt um das technische Bild und Behavioural Finance-Aspekte vor allem für Anleger, die eine hohe Partizipation am Aktienmarkt mit soliden Basiswerten sowie einem aktiven Management wünschen und eine entsprechende Risikobereitschaft mitbringen.';

  // Top 10 Stock Positions
  const top10Stocks = effectiveData.top10Stocks || [
    { name: 'Reg Shs Tudor Gold Corp', percentage: 3.44 },
    { name: 'SBI Holdings Inc', percentage: 2.65 },
    { name: 'Intesa Sanpaolo S.p.A.', percentage: 2.46 },
    { name: 'ABN AMRO Group N.V. Shs Depositar...', percentage: 2.41 },
    { name: 'Glb X Silv USD', percentage: 2.35 },
    { name: 'Italgas SpA', percentage: 2.34 },
    { name: 'Nexstar Media Group Inc', percentage: 2.34 },
    { name: 'SUMITOMO MITSUI FIN.', percentage: 2.30 },
    { name: 'Oji Paper Co Ltd', percentage: 2.30 },
    { name: 'Haseko Corp', percentage: 2.27 },
  ];

  const formatStockPercentage = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) return '';
    
    // If it's a string, preserve it as-is (removing % if present)
    if (typeof value === 'string') {
      const normalized = value.trim();
      return normalized.endsWith('%') ? normalized.slice(0, -1) : normalized;
    }
    
    // If it's a number, format to preserve trailing zeros
    const numValue = Number(value);
    if (isNaN(numValue)) return '';
    
    // Convert to string to check original precision
    const strValue = String(value);
    const decimalIndex = strValue.indexOf('.');
    
    if (decimalIndex === -1) {
      // No decimal point, format with 2 decimal places for consistency
      return numValue.toFixed(2);
    }
    
    // Has decimal point - determine precision from original string representation
    const decimalPart = strValue.substring(decimalIndex + 1);
    const precision = decimalPart.length;
    
    // Format with the original precision, but ensure at least 2 decimal places
    // This preserves values like 4.601 (3 decimals) but ensures 5.1 becomes 5.10
    const minPrecision = Math.max(precision, 2);
    return numValue.toFixed(minPrecision);
  };

  // Sector Allocation
  const sectorAllocation = effectiveData.sectorAllocation || [
    { label: 'Finanzwesen', value: 27.6 },
    { label: 'Industrie', value: 18.2 },
    { label: 'Nicht-Basiskonsumgüter', value: 9.7 },
    { label: 'Basisgüter', value: 9.5 },
    { label: 'Hilfs- & Betriebsstoffe', value: 7.7 },
    { label: 'Sonstige', value: 27.3 },
  ];

  // Currency Allocation
  const currencyAllocation = effectiveData.currencyAllocation || [
    { label: 'JPY', value: 38.7 },
    { label: 'USD', value: 9.4 },
    { label: 'CAD', value: 9.7 },
    { label: 'EUR', value: 35.4 },
    { label: 'CHF', value: 6.2 },
    { label: 'Sonstige', value: 0.6 },
  ];

  // Asset Allocation
  const assetAllocation = effectiveData.assetAllocation || [
    { label: 'Aktien', value: 96.5 },
    { label: 'Kasse', value: 37.2 },
    { label: 'Übrige Fonds', value: 1.9 },
    { label: 'Derivate', value: -35.6 },
  ];

  // Target Group, Investment Horizon, Risk Indicator
  const targetGroup = effectiveData.targetGroup || 'Der Teilfonds eignet sich für aktienorientierte Anleger, die in Investmentfonds ein geeignetes Mittel zur Teilnahme an der Kapitalmarktentwicklung sehen. Er eignet sich ferner für den eher erfahrenen Anleger, der damit bestimmte Anlageziele verfolgt. Erfahrung mit Kapitalmarktprodukten ist nicht erforderlich. Das Anlageziel des Teilfonds ist ausgerichtet auf die Vermögensbildung. In einem Anleger-Portfolio kann er die Rolle eines Basisinvestments einnehmen.';

  const investmentHorizon = effectiveData.investmentHorizon || 'Der Anleger in diesem Teilfonds sollte eine Anlage von drei bis fünf Jahren planen, da der Anleger in der Lage sein muss, Verluste hinzunehmen.';

  const riskIndicator = effectiveData.riskIndicator || 5; // 1-7 scale

  // Opportunities and Risks
  const opportunities = effectiveData.opportunities || [
    'Aktien bieten langfristig ein überdurchschnittliches Renditepotential.',
    'Die internationale Ausrichtung ermöglicht eine breite Streuung der Investments.',
    'Das aktive Portfoliomanagement und die flexible Ausrichtung bieten ein attraktives Chancen-/Risikoprofil.',
    'Der Einsatz von Derivaten kann helfen, das Portfolio bei negativen Marktentwicklungen abzusichern.',
    'Durch Hebelwirkungen können überproportionale Wertsteigerungen erreicht werden.',
  ];

  const risks = effectiveData.risks || [
    'Die Werte von Aktien können stark schwanken und es sind deutliche Verluste möglich.',
    'Durch die breite Streuung der Investments kann es zu einer begrenzten Teilnahme an der Wertentwicklung einzelner Regionen kommen.',
    'Der Einsatz von Derivaten kann zu erheblichen Verlusten im Portfolio führen.',
    'Die Geldpolitik der Notenbanken kann den Markt unerwartet beeinflussen. Fehlentscheidungen bei Auswahl und Zeitpunkt der Investitionen können nicht ausgeschlossen werden.',
  ];

  // Sales Prospectus
  const salesProspectus = effectiveData.salesProspectus || 'Die vollständigen Angaben zu dem Fonds, einschließlich den mit einer Fondsanlage verbundenen Chancen und Risiken sind dem jeweils aktuellen Verkaufsprospekt nebst Verwaltungsreglement oder Satzung sowie ggf. dem Basisinformationsblatt (PRIIP-KIDs), ergänzt durch den letzten geprüften Jahresbericht bzw. den letzten Halbjahresbericht zu entnehmen. Diese Unterlagen stellen die alleinverbindliche Grundlage für den Kauf von Fondsanteilen dar.';

  // Legal Notes
  const legalNotesData = effectiveData.legalNotes || {
    verwahrstelle: 'Kreissparkasse Köln',
    verwaltungsgesellschaft: 'Monega Kapitalanlagegesellschaft mbH',
    text: 'Dies ist eine Werbemitteilung. Die in diesem Factsheet angegebenen Fondsinformationen sind zu allgemeinen Informationszwecken erstellt worden und dienen einer ersten Orientierung zu dem Produkt. Sie ersetzen weder eigene Marktrecherchen noch sonstige rechtliche, steuerliche oder finanzielle Informationen oder Beratung. Das Factsheet stellt keine Kauf- oder Verkaufsaufforderung oder Anlageberatung dar. Das Factsheet enthält nicht alle für wirtschaftlich bedeutende Entscheidungen wesentlichen Angaben und kann von Informationen und Einschätzungen anderer Quellen oder Marktteilnehmer abweichen.\n\nDie vollständigen Angaben zu dem Fonds, einschließlich den mit einer Fondsanlage verbundenen Chancen und Risiken sind dem jeweils aktuellen Verkaufsprospekt nebst Verwaltungsreglement oder Satzung sowie ggf. dem Basisinformationsblatt (PRIIP-KIDs), ergänzt durch den letzten geprüften Jahresbericht bzw. den letzten Halbjahresbericht zu entnehmen. Diese Unterlagen stellen die alleinverbindliche Grundlage für den Kauf von Fondsanteilen dar. Sie sind in elektronischer oder gedruckter Form in deutscher Sprache kostenlos bei der Axxion S.A., 15 rue de Flaxweiler, L-6776 Grevenmacher erhältlich.\n\nDer letzte Nettoinventarwert kann über die Internetseite der Axxion S.A. abgerufen werden.\n\nBitte beachten Sie: Wertentwicklungen in der Vergangenheit sind kein verlässlicher Indikator für die künftige Wertentwicklung. Investmentfonds unterliegen marktbedingten Kursschwankungen, die zu Verlusten, bis hin zum Totalverlust des eingesetzten Kapitals, führen können. Es wird ausdrücklich auf die Risikohinweise des Verkaufsprospektes verwiesen. Die zukünftige Wertentwicklung unterliegt der Besteuerung, die von der persönlichen Situation des jeweiligen Anlegers abhängig ist und sich in der Zukunft ändern kann.\n\nDie Bruttowertentwicklung (BVI-Methode) berücksichtigt bereits alle auf Fondsebene anfallenden Kosten und geht von einer Wiederanlage eventueller Ausschüttung aus. Sofern nicht anders angegeben, entsprechen alle dargestellten Wertentwicklungen der Bruttowertentwicklung. Die Nettowertentwicklung geht von einer Modellrechnung mit einem investierten Betrag von 1.000 €, dem max. Ausgabeaufschlag sowie einem Rücknahmeabschlag (falls vorgesehen, siehe Stammdaten) aus. Sie berücksichtigt keine anderen eventuell anfallenden individuellen Kosten des Anlegers, wie beispielsweise eine Depotführungsgebühr.\n\nDie ausgegebenen Anteile dieses Fonds dürfen nur in solchen Rechtsordnungen zum Kauf angeboten oder verkauft werden, in denen ein solches Angebot oder ein solcher Verkauf zulässig ist. So dürfen die Anteile dieses Fonds weder innerhalb der USA noch an oder für Rechnung von US Staatsbürgern oder in den USA ansässigen US-Personen zum Kauf angeboten oder an diese verkauft werden. Dieses Dokument und die in ihm enthaltenen Informationen dürfen nicht in den USA verbreitet werden. Die Verbreitung und Veröffentlichung dieses Dokumentes sowie das Angebot oder ein Verkauf der Anteile können auch in anderen Rechtsordnungen Beschränkungen unterworfen sein.',
  };
  // Helper function to split text intelligently
  const splitLegalNotesText = (text: string): string[] => {
    // First, try to split by paragraphs
    const paragraphs = text.split('\n\n').filter((p: string) => p.trim().length > 0);
    
    // If we have multiple paragraphs, use them
    if (paragraphs.length > 1) {
      return paragraphs;
    }
    
    // If it's one long paragraph, split by character count (approximately 2000 chars per part)
    // Try to split at sentence boundaries
    const maxCharsPerPart = 2000;
    const fullText = paragraphs[0] || text;
    
    if (fullText.length <= maxCharsPerPart) {
      return [fullText];
    }
    
    // Split into chunks, trying to break at sentence boundaries
    const chunks: string[] = [];
    let currentChunk = '';
    const sentences = fullText.split(/([.!?]\s+)/);
    
    for (let i = 0; i < sentences.length; i += 2) {
      const sentence = sentences[i] + (sentences[i + 1] || '');
      const potentialChunk = currentChunk + sentence;
      
      if (potentialChunk.length > maxCharsPerPart && currentChunk.length > 0) {
        chunks.push(currentChunk.trim());
        currentChunk = sentence;
      } else {
        currentChunk = potentialChunk;
      }
    }
    
    if (currentChunk.trim().length > 0) {
      chunks.push(currentChunk.trim());
    }
    
    // If still too long, split by character count
    if (chunks.length === 0 || chunks[0].length > maxCharsPerPart * 1.5) {
      const charChunks: string[] = [];
      for (let i = 0; i < fullText.length; i += maxCharsPerPart) {
        let chunk = fullText.substring(i, i + maxCharsPerPart);
        // Try to break at word boundary
        if (i + maxCharsPerPart < fullText.length) {
          const lastSpace = chunk.lastIndexOf(' ');
          if (lastSpace > maxCharsPerPart * 0.8) {
            chunk = chunk.substring(0, lastSpace);
            i -= (maxCharsPerPart - lastSpace);
          }
        }
        charChunks.push(chunk.trim());
      }
      return charChunks;
    }
    
    return chunks.length > 0 ? chunks : [fullText];
  };

  const legalNotesTerms = legalNotesData.text.split(' ');
  const firstPartLegalNotes = legalNotesTerms.slice(0, 180).join(' ');
  const secondPartLegalNotes = legalNotesTerms.slice(180).join(' ');

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page} wrap>
        {/* Header + two-column top block stay together on page 1 */}
        <View wrap={false}>
        {/* Header */}
        <View style={pdfStyles.header}>
          {/* Background Image */}
          {bannerImageSrc && (
            <Image
              src={bannerImageSrc}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          )}
          <View style={pdfStyles.headerLeft}>
            {logoSrc ? (
              <View style={pdfStyles.logoBox}>
                <Image src={logoSrc} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </View>
            ) : (
              <View style={pdfStyles.logoBox}>
                <Text style={pdfStyles.logoText}>FONDS HUB</Text>
              </View>
            )}
          </View>
          <View style={pdfStyles.headerRight}>
            <View style={pdfStyles.dateBadge}>
              <Text style={pdfStyles.dateBadgeText}>FACTSHEET {formattedDate}</Text>
            </View>
          </View>
        </View>

        {/* Main Content */}
        <View style={pdfStyles.mainContent}>
          {/* Split Content */}
          <View style={pdfStyles.splitContent}>
            {/* Left Column */}
            <View style={pdfStyles.leftColumn}>
              {/* Title and Subtitle */}
              <View>
                <Text style={pdfStyles.title}>{effectiveTitle}</Text>
                <Text style={pdfStyles.subtitle}>{effectiveSubtitle}</Text>
              </View>

              {/* Card */}
              <View style={pdfStyles.card}>
                {/* Card Header */}
                <View style={pdfStyles.cardHeader}>
                  <Text style={pdfStyles.cardHeaderTitle}>{sectionHeaders.chartImageTitle || 'Chart-Platzhalter'}</Text>
                </View>

                {/* Card Body - Chart Image */}
                <View style={pdfStyles.cardBody}>
                  {chartImageSrc ? (
                    <View style={pdfStyles.chartContainer}>
                      <Image src={chartImageSrc} style={{ width: '100%', height: '200px' }} />
                    </View>
                  ) : resolvedChart1ImageUrl ? (
                    <View style={pdfStyles.chartContainer}>
                      <Image src={resolvedChart1ImageUrl} style={{ width: '100%', height: 'auto' }} />
                    </View>
                  ) : null}
                </View>

                {/* Fund Manager Section */}
                <View style={pdfStyles.fundManagerSection}>
                  <View style={pdfStyles.fundManagerContent}>
                    {manImageSrc ? (
                      <Image src={manImageSrc} style={pdfStyles.fundManagerPhoto} />
                    ) : (
                      <View style={pdfStyles.fundManagerPhotoPlaceholder} />
                    )}
                    <View style={pdfStyles.fundManagerInfo}>
                      <Text style={pdfStyles.fundManagerName}>{fundManager.name}</Text>
                      <Text style={pdfStyles.fundManagerCompany}>{fundManager.company}</Text>
                      <Text style={pdfStyles.fundManagerResearch}>{fundManager.research}</Text>
                      <Text style={pdfStyles.fundManagerCompany2}>{fundManager.company2}</Text>
                      <Text style={pdfStyles.fundManagerPhone}>Telefon: {fundManager.phone}</Text>
                      {
                        fundManager.hedgingRatio && (
                          <View style={pdfStyles.hedgingButton}>
                            <Text style={pdfStyles.hedgingButtonText}>
                              Absicherungsquote aktuell: {fundManager.hedgingRatio}
                            </Text>
                          </View>
                        )
                      }
                    </View>
                  </View>
                </View>
              </View>
            </View>

            {/* Right Column - Sidebar */}
            <View style={pdfStyles.rightColumn}>
              <View style={pdfStyles.sidebar}>
                {/* Stammdaten */}
                <View style={pdfStyles.sidebarSection}>
                  <Text style={pdfStyles.sidebarSectionTitle}>{sectionHeaders.stammdaten}</Text>
                  <View style={pdfStyles.kvGroup}>
                    <View style={pdfStyles.kvRow}>
                      <Text style={pdfStyles.kvLabel}>Fondskategorie</Text>
                      <Text style={pdfStyles.kvValue}>{getDisplayValue('masterData', 'fondskategorie', 'Aktienfonds')}</Text>
                    </View>
                    <View style={pdfStyles.kvRow}>
                      <Text style={pdfStyles.kvLabel}>Auflagedatum</Text>
                      <Text style={pdfStyles.kvValue}>{getDisplayValue('masterData', 'auflagedatum', '02.09.2024')}</Text>
                    </View>
                    <View style={pdfStyles.kvRow}>
                      <Text style={pdfStyles.kvLabel}>WKN</Text>
                      <Text style={pdfStyles.kvValue}>{getDisplayValue('masterData', 'wkn', 'A3EWBE')}</Text>
                    </View>
                    <View style={pdfStyles.kvRow}>
                      <Text style={pdfStyles.kvLabel}>ISIN</Text>
                      <Text style={pdfStyles.kvValue}>{getDisplayValue('masterData', 'isin', 'DE000A3EWBE8')}</Text>
                    </View>
                    <View style={pdfStyles.kvRow}>
                      <Text style={pdfStyles.kvLabel}>Erstausgabepreis</Text>
                      <Text style={pdfStyles.kvValue}>{getDisplayValue('masterData', 'erstausgabepreis', '148,37 EUR')}</Text>
                    </View>
                    <View style={pdfStyles.kvRow}>
                      <Text style={pdfStyles.kvLabel}>Fondsvolumen</Text>
                      <Text style={pdfStyles.kvValue}>{getDisplayValue('masterData', 'fondsvolumen', '9,96 Mio. EUR')}</Text>
                    </View>
                    <View style={pdfStyles.kvRow}>
                      <Text style={pdfStyles.kvLabel}>Kurs (NAV)</Text>
                      <Text style={pdfStyles.kvValue}>{getDisplayValue('masterData', 'kursNav', '146,39 EUR')}</Text>
                    </View>
                    <View style={pdfStyles.kvRow}>
                      <Text style={pdfStyles.kvLabel}>Fondswährung</Text>
                      <Text style={pdfStyles.kvValue}>{getDisplayValue('masterData', 'fondswaehrung', 'EUR')}</Text>
                    </View>
                    <View style={pdfStyles.kvRow}>
                      <Text style={pdfStyles.kvLabel}>Fondsdomizil</Text>
                      <Text style={pdfStyles.kvValue}>{getDisplayValue('masterData', 'fondsdomizil', 'DE')}</Text>
                    </View>
                    <View style={pdfStyles.kvRow}>
                      <Text style={pdfStyles.kvLabel}>Geschäftsjahresende</Text>
                      <Text style={pdfStyles.kvValue}>{getDisplayValue('masterData', 'geschaeftsjahresende', '31-Aug.')}</Text>
                    </View>
                    <View style={pdfStyles.kvRow}>
                      <Text style={pdfStyles.kvLabel}>Ertragsverwendung</Text>
                      <Text style={pdfStyles.kvValue}>{getDisplayValue('masterData', 'ertragsverwendung', 'Thesaurierend')}</Text>
                    </View>
                    <View style={pdfStyles.kvRow}>
                      <Text style={[pdfStyles.kvLabel, pdfStyles.kvWidth30]}>Rechtsform</Text>
                      <Text style={pdfStyles.kvValue}>{getDisplayValue('masterData', 'rechtsform', 'OGAW-Sondervermögen')}</Text>
                    </View>
                    <View style={pdfStyles.kvRow}>
                      <Text style={pdfStyles.kvLabel}>Risiko-/Ertragsprofil (SRI 1-7)*</Text>
                      <Text style={pdfStyles.kvValue}>{getDisplayValue('masterData', 'risikoErtragsprofil', '4')}</Text>
                    </View>
                    <View style={pdfStyles.kvRow}>
                      <Text style={pdfStyles.kvLabel}>Sparplanfähig</Text>
                      <Text style={pdfStyles.kvValue}>{getDisplayValue('masterData', 'sparplanfaehig', 'ja')}</Text>
                    </View>
                    <View style={pdfStyles.kvRow}>
                      <Text style={[pdfStyles.kvLabel, pdfStyles.kvWidth70]}>Mindestzeichnungbetrag</Text>
                      <Text style={pdfStyles.kvValue}>{getDisplayValue('masterData', 'mindestzeichnungbetrag', '100.000 EUR')}</Text>
                    </View>
                  </View>
                </View>

                {/* Gebühren */}
                <View style={pdfStyles.sidebarSection}>
                  <Text style={pdfStyles.sidebarSectionTitle}>Gebühren</Text>
                  <View style={pdfStyles.kvGroup}>
                    <View style={pdfStyles.kvRow}>
                      <Text style={pdfStyles.kvLabel}>Ausgabeaufschlag</Text>
                      <Text style={pdfStyles.kvValue}>{getDisplayValue('fees', 'ausgabeaufschlag', '0% (MAX 5%)')}</Text>
                    </View>
                    <View style={pdfStyles.kvRow}>
                      <Text style={pdfStyles.kvLabel}>Laufende Kosten (TER)</Text>
                      <Text style={pdfStyles.kvValue}>{getDisplayValue('fees', 'laufendeKostenTer', '1,94%')}</Text>
                    </View>
                    <View style={pdfStyles.kvRow}>
                      <Text style={pdfStyles.kvLabel}>davon Verwaltungsgebühr</Text>
                      <Text style={[pdfStyles.kvValue, pdfStyles.kvWidth30]}>{getDisplayValue('fees', 'davonVerwaltungsgebuehr', '1,23% (MAX 2%)')}</Text>
                    </View>
                  </View>
                </View>
              </View>
            </View>
          </View>
        </View>
        </View>

        <View style={pdfStyles.mainContent}>
          {/* Page-1 commentary card — closed with full bottom radius (no mid-card clip) */}
          {firstPartCommentary ? (
            <View style={pdfStyles.card} wrap={false}>
              <View style={pdfStyles.cardHeader}>
                <Text style={pdfStyles.cardHeaderTitle}>{sectionHeaders.monthlyCommentary}</Text>
              </View>
              <View style={pdfStyles.cardBody}>
                <Text style={pdfStyles.commentaryText}>{firstPartCommentary}</Text>
              </View>
            </View>
          ) : null}

          {/* Page-2 continuation — top padding + top radius (mainContent padding does not repeat) */}
          {secondPartCommentary ? (
            <View style={[pdfStyles.card, pdfStyles.commentaryCardContinued]} wrap={false}>
              <View style={pdfStyles.cardBody}>
                <Text style={pdfStyles.commentaryText}>{secondPartCommentary}</Text>
              </View>
            </View>
          ) : null}

          <View style={pdfStyles.card} wrap={false}>
            <View style={pdfStyles.cardHeader}>
              <Text style={pdfStyles.cardHeaderTitle}>{sectionHeaders.indexedChart}</Text>
            </View>
            <View style={pdfStyles.cardBody}>
              {performanceNotice ? (
                <View style={pdfStyles.noticeBox}>
                  <Text style={pdfStyles.noticeText}>{performanceNotice}</Text>
                </View>
              ) : resolvedChart2ImageUrl ? (
                <View style={pdfStyles.chartSectionContainer}>
                  <Image src={resolvedChart2ImageUrl} style={{ width: '100%', height: 'auto' }} />
                </View>
              ) : (
                <View style={pdfStyles.chartPlaceholder}>
                  <Text style={pdfStyles.placeholderText}>Chart wird hier angezeigt</Text>
                </View>
              )}
            </View>
          </View>

          <View style={pdfStyles.card} wrap={false}>
            <View style={pdfStyles.cardHeader}>
              <Text style={pdfStyles.cardHeaderTitle}>{sectionHeaders.cumulativePerformance}</Text>
            </View>
            <View style={pdfStyles.cardBody}>
              <View style={pdfStyles.performanceTable}>
                <View style={pdfStyles.performanceTableHeader}>
                  {cumulativePerformanceData.labels.map((label, index) => (
                    <View key={index} style={pdfStyles.performanceTableHeaderCell}>
                      <Text style={pdfStyles.performanceTableHeaderText}>{label}</Text>
                    </View>
                  ))}
                </View>
                {cumulativePerformanceData.values.map((row, rowIndex) => (
                  <View key={rowIndex} style={pdfStyles.performanceTableRow}>
                    {row.map((cell: any, colIndex: number) => {
                      if (performanceNotice) {
                        return (
                          <View key={colIndex} style={pdfStyles.performanceTableCell}>
                            <Text style={pdfStyles.performanceTableValue}>–</Text>
                          </View>
                        );
                      }
                      const columnType = cumulativePerformanceData.columns?.[colIndex]?.type || 'text';
                      const isDynamic = cell?.isDynamic && cell?.fields?.length;
                      const cellKey = `cumulative-${rowIndex}-${colIndex}`;
                      const fetchedValue = isDynamic && cellDataCache ? cellDataCache.get(cellKey) : null;

                      let displayValue = '';
                      if (isDynamic && fetchedValue !== undefined && fetchedValue !== null) {
                        displayValue = String(fetchedValue);
                      } else if (columnType === 'text') {
                        displayValue = typeof cell === 'string' ? cell : (cell?.text || '');
                      } else if (columnType === 'link') {
                        displayValue = cell?.linkText || '';
                      } else if (columnType === 'avatar') {
                        displayValue = typeof cell === 'string' ? cell : '';
                      }

                      return (
                        <View key={colIndex} style={pdfStyles.performanceTableCell}>
                          <Text style={pdfStyles.performanceTableValue}>
                            {displayValue} {displayValue && !displayValue.includes('%') ? '%' : ''}
                          </Text>
                        </View>
                      );
                    })}
                  </View>
                ))}
              </View>
              {performanceNotice ? <Text style={pdfStyles.noticeInline}>{performanceNotice}</Text> : null}
            </View>
          </View>
        </View>
      </Page>

      {/* Rolling Performance Chart (was page 3) */}
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.mainContent}>
          <View style={pdfStyles.card}>
            <View style={pdfStyles.cardHeader}>
              <Text style={pdfStyles.cardHeaderTitle}>{sectionHeaders.rollingPerformance}</Text>
            </View>
            <View style={pdfStyles.cardBody}>
              {performanceNotice ? (
                <View style={pdfStyles.noticeBox}>
                  <Text style={pdfStyles.noticeText}>{performanceNotice}</Text>
                </View>
              ) : resolvedChart3ImageUrl ? (
                <View style={pdfStyles.chartSectionContainer}>
                  <Image src={resolvedChart3ImageUrl} style={{ width: '100%', height: 'auto' }} />
                </View>
              ) : (
                <View style={pdfStyles.chartPlaceholder}>
                  <Text style={pdfStyles.placeholderText}>
                    Chart wird hier angezeigt
                  </Text>
                </View>
              )}

              {/* Explanatory Text */}
              <View style={pdfStyles.explanatoryText}>
                <Text style={pdfStyles.explanatoryTextTitle}>{sectionHeaders.explanatoryTextTitle || 'Erläuterungen zur Wertentwicklung'}</Text>
                {performanceNotice ? (
                  <Text style={pdfStyles.noticeInline}>{performanceNotice}</Text>
                ) : null}
                <Text style={pdfStyles.explanatoryTextContent}>
                  {effectiveData.explanatoryText || 'Die Bruttorendite (BVI-Methode) berücksichtigt alle auf Fondsebene anfallenden Kosten, die vergangene Wertentwicklung berücksichtigt den Ausgabeaufschlag. Weitere Kosten können auf individuell anfallen (z.B. Depotkosten). Modellrechnung (netto): Ein Anleger möchte für 1.000,00 EUR Anteile erwerben. Bei einem Ausgabeaufschlag von 5,00 % muss er insgesamt 1.052,63 EUR aufwenden. Die Depotkosten ergeben sich aus dem Preis- und Leistungsverzeichnis ihrer depotführenden Bank. Bitte beachten Sie, dass der Referenzindex nur informatorischen Charakter hat und begrenzt keine Verpflichtung des Fondsmanagers, den Index oder dessen Wertentwicklung nachzubilden oder zu erreichende Mindestrendite darstellt. Quelle: FWW FundDataWeb, Stand: 29.08.2025. Bei unvollständiger Wertentwicklung.'}
                </Text>
              </View>
            </View>
          </View>

          <View style={pdfStyles.twoColumn}>
            {/* Investment Strategy Card */}
            <View style={[pdfStyles.card, pdfStyles.w60]}>
              <View style={pdfStyles.cardHeader}>
                <Text style={pdfStyles.cardHeaderTitle}>{sectionHeaders.investmentStrategy}</Text>
              </View>
              <View style={pdfStyles.cardBody}>
                <Text style={pdfStyles.strategyText}>
                  {investmentStrategy}
                </Text>
              </View>
            </View>

            {/* Awards Card */}
            <View style={[pdfStyles.card, pdfStyles.twoColumnSection]}>
              <View style={pdfStyles.cardHeader}>
                <Text style={pdfStyles.cardHeaderTitle}>{sectionHeaders.awards}</Text>
              </View>
              <View style={[pdfStyles.cardBody, { flex: 1 }]}>
                {awardsImageSrc ? (
                  <View style={pdfStyles.chartContainer}>
                    <Image src={awardsImageSrc} style={{ width: '100%', height: '200px', objectFit: 'cover' }} />
                  </View>
                ) : null}
              </View>
            </View>
          </View>
        </View>
      </Page>

      {/* Fourth Page - Four Cards Section */}
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.mainContent}>
          <View style={pdfStyles.fourCardGrid}>
            {/* Top 10 Stock Positions Card */}
            <View style={[pdfStyles.card, pdfStyles.allocationCard, { marginBottom: 16 }]}>
              <View style={pdfStyles.cardHeader}>
                <Text style={pdfStyles.cardHeaderTitle}>{sectionHeaders.top10Stocks}</Text>
              </View>
              <View style={pdfStyles.cardBody}>
                <View style={pdfStyles.stockList}>
                  {top10Stocks.map((stock, index) => (
                    <View key={index} style={pdfStyles.stockListItem}>
                      <Text style={pdfStyles.stockListNumber}>{index + 1}.</Text>
                      <Text style={pdfStyles.stockListName}>{stock.name}</Text>
                      <Text style={pdfStyles.stockListPercentage}>{formatStockPercentage(stock.percentage)}%</Text>
                    </View>
                  ))}
                </View>
              </View>
            </View>

            {/* Sector Allocation Card */}
            <View style={[pdfStyles.card, pdfStyles.allocationCard, { marginBottom: 16 }]}>
              <View style={pdfStyles.cardHeader}>
                <Text style={pdfStyles.cardHeaderTitle}>{sectionHeaders.sectorAllocation}</Text>
              </View>
              <View style={[pdfStyles.cardBody, pdfStyles.alignCenter, { flex: 1 }]}>
                {resolvedPieChart1ImageUrl ? (
                  <View style={pdfStyles.piechartSectionContainer}>
                    <Image src={resolvedPieChart1ImageUrl} style={{ width: '100%', height: 'auto' }} />
                  </View>
                ) : (
                  renderAllocationFallback(sectorAllocation)
                )}
              </View>
            </View>

            {/* Currency Allocation Card */}
            <View style={[pdfStyles.card, pdfStyles.allocationCard, { height: 200 }]}>
              <View style={pdfStyles.cardHeader}>
                <Text style={pdfStyles.cardHeaderTitle}>{sectionHeaders.currencyAllocation}</Text>
              </View>
              <View style={[pdfStyles.cardBody, pdfStyles.alignCenter, { flex: 1 }]}>
                {resolvedPieChart2ImageUrl ? (
                  <View style={pdfStyles.piechartSectionContainer}>
                    <Image src={resolvedPieChart2ImageUrl} style={{ width: '100%', height: 'auto' }} />
                  </View>
                ) : (
                  renderAllocationFallback(currencyAllocation)
                )}
              </View>
            </View>

            {/* Asset Allocation Card */}
            <View style={[pdfStyles.card, pdfStyles.allocationCard, { height: 200 }]}>
              <View style={pdfStyles.cardHeader}>
                <Text style={pdfStyles.cardHeaderTitle}>{sectionHeaders.assetAllocation}</Text>
              </View>
              <View style={[pdfStyles.cardBody, pdfStyles.alignCenter]}>
                {resolvedPieChart3ImageUrl ? (
                  <View style={pdfStyles.piechartSectionContainer}>
                    <Image src={resolvedPieChart3ImageUrl} style={{ width: '100%', height: 'auto' }} />
                  </View>
                ) : (
                  renderAllocationFallback(assetAllocation)
                )}
              </View>
            </View>
          </View>

          {/* Three Panels Section - Target Group, Investment Horizon, Risk Indicator */}
          <View style={pdfStyles.threePanelGrid}>
            {/* Target Group Panel */}
            <View style={[pdfStyles.card, pdfStyles.blueCard]}>
              <View style={[pdfStyles.cardHeader, pdfStyles.blueCardHeader]}>
                <Text style={[pdfStyles.cardHeaderTitle, pdfStyles.colorWhite]}>ZIELGRUPPE</Text>
              </View>
              <View style={[pdfStyles.cardBody, pdfStyles.blueCardBody]}>
                <Text style={pdfStyles.cardBodyText}>
                  {targetGroup}
                </Text>
              </View>
            </View>

            {/* Investment Horizon Panel */}
            <View style={[pdfStyles.card, pdfStyles.blueCard]}>
              <View style={[pdfStyles.cardHeader, pdfStyles.blueCardHeader]}>
                <Text style={[pdfStyles.cardHeaderTitle, pdfStyles.colorWhite]}>ANLAGEHORIZONT</Text>
              </View>
              <View style={[pdfStyles.cardBody, pdfStyles.blueCardBody]}>
                <Text style={pdfStyles.cardBodyText}>
                  {investmentHorizon}
                </Text>
              </View>
            </View>

            {/* Risk Indicator Panel */}
            <View style={[pdfStyles.card, pdfStyles.blueCard]}>
              <View style={[pdfStyles.cardHeader, pdfStyles.blueCardHeader]}>
                <Text style={[pdfStyles.cardHeaderTitle, pdfStyles.colorWhite]}>RISIKOINDIKATOR</Text>
              </View>
              <View style={[pdfStyles.cardBody, pdfStyles.blueCardBody]}>
                {/* Risk Scale Labels */}
                <View style={pdfStyles.riskScaleLabels}>
                  <Text style={pdfStyles.riskScaleLabel}>Geringeres Risiko</Text>
                  <Text style={pdfStyles.riskScaleLabel}>Höheres Risiko</Text>
                </View>

                {/* Risk Scale Boxes */}
                <View style={pdfStyles.riskScaleBoxes}>
                  {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                    <View
                      key={num}
                      style={num === riskIndicator ? [pdfStyles.riskScaleBox, pdfStyles.riskScaleBoxActive] : pdfStyles.riskScaleBox}
                    >
                      <Text
                        style={num === riskIndicator ? [pdfStyles.riskScaleBoxNumber, pdfStyles.riskScaleBoxNumberActive] : pdfStyles.riskScaleBoxNumber}
                      >
                        {num}
                      </Text>
                    </View>
                  ))}
                </View>

                {/* Gradient Bar */}
                <View style={pdfStyles.riskGradientBar}>
                </View>

                {/* Return Indicator Labels */}
                <View style={pdfStyles.riskScaleLabels}>
                  <Text style={pdfStyles.riskScaleLabel}>Potentiell geringerer Ertrag</Text>
                  <Text style={pdfStyles.riskScaleLabel}>Potentiell höherer Ertrag</Text>
                </View>

                {/* Disclaimer Text */}
                <Text style={pdfStyles.riskDisclaimerText}>
                  Die zur Berechnung des synthetischen Indikators verwendeten historischen Daten sind kein verlässlicher Hinweis auf das künftige Risiko- und Ertragsprofil der Anteilsklasse. Die periodische Einstufung kann Änderungen unterliegen und sich im Laufe der Zeit daher verändern.
                </Text>
              </View>
            </View>
          </View>
        </View>
      </Page>

      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.mainContent}>
          {/* Opportunities and Risks Section */}
          <View style={pdfStyles.twoColumn}>
            {/* Opportunities Card */}
            <View style={[pdfStyles.card, pdfStyles.twoColumnSection]}>
              <View style={[pdfStyles.cardHeader, pdfStyles.blueCardHeader]}>
                <Text style={[pdfStyles.cardHeaderTitle, pdfStyles.colorWhite]}>{sectionHeaders.opportunities}</Text>
              </View>
              <View style={pdfStyles.cardBody}>
                {opportunities.map((item: string, index: number) => (
                  <View key={index} style={pdfStyles.bulletItem}>
                    <Text style={pdfStyles.bulletPoint}>+</Text>
                    <Text style={pdfStyles.bulletText}>{item}</Text>
                  </View>
                ))}
              </View>
            </View>

            {/* Risks Card */}
            <View style={[pdfStyles.card, pdfStyles.twoColumnSection]}>
              <View style={[pdfStyles.cardHeader, pdfStyles.blueCardHeader]}>
                <Text style={[pdfStyles.cardHeaderTitle, pdfStyles.colorWhite]}>{sectionHeaders.risks}</Text>
              </View>
              <View style={pdfStyles.cardBody}>
                {risks.map((item: string, index: number) => (
                  <View key={index} style={pdfStyles.bulletItem}>
                    <Text style={pdfStyles.bulletPoint}>-</Text>
                    <Text style={pdfStyles.bulletText}>{item}</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>

          {/* Sales Prospectus Section */}
          <View style={pdfStyles.card}>
            <View style={[pdfStyles.cardHeader, pdfStyles.blueCardHeader]}>
              <Text style={[pdfStyles.cardHeaderTitle, pdfStyles.colorWhite]}>{sectionHeaders.salesProspectus}</Text>
            </View>
            <View style={pdfStyles.cardBody}>
              <Text style={pdfStyles.cardBodyText}>{salesProspectus}</Text>
            </View>
          </View>

          {/* Legal Notes Section - First Part */}
          <View style={pdfStyles.card}>
            <View style={[pdfStyles.cardHeader, pdfStyles.blueCardHeader]}>
              <Text style={[pdfStyles.cardHeaderTitle, pdfStyles.colorWhite]}>{sectionHeaders.legalNotes}</Text>
            </View>
            <View style={pdfStyles.cardBody}>
              {/* Key-Value Pairs */}
              <View style={pdfStyles.legalKvRow}>
                <Text style={pdfStyles.legalKvLabel}>Verwahrstelle:</Text>
                <Text style={pdfStyles.legalKvValue}>{legalNotesData.verwahrstelle}</Text>
              </View>
              <View style={pdfStyles.legalKvRow}>
                <Text style={pdfStyles.legalKvLabel}>Verwaltungsgesellschaft:</Text>
                <Text style={pdfStyles.legalKvValue}>{legalNotesData.verwaltungsgesellschaft}</Text>
              </View>
              
              {/* Legal Text - First Part */}
              <Text style={pdfStyles.legalText}>
                {firstPartLegalNotes}
              </Text>
            </View>
          </View>
        </View>
      </Page>

      {/* Legal Notes Section - Second Part (if exists) */}
      {secondPartLegalNotes.length > 0 && (
        <Page size="A4" style={pdfStyles.page}>
          <View style={pdfStyles.mainContent}>
            <View style={pdfStyles.card}>
              <View style={[pdfStyles.cardHeader, pdfStyles.blueCardHeader]}>
                <Text style={[pdfStyles.cardHeaderTitle, pdfStyles.colorWhite]}>{sectionHeaders.legalNotes}</Text>
              </View>
              <View style={pdfStyles.cardBody}>
                <Text style={pdfStyles.legalText}>
                  {secondPartLegalNotes}
                </Text>
              </View>
            </View>
          </View>
        </Page>
      )}
    </Document>
  );
};

export const PdfPreviewDialog: FC<PdfPreviewDialogProps> = ({
  open,
  onClose,
  data,
  template,
  title = 'PDF Preview',
  isin,
}) => {
  const [logoSrc, setLogoSrc] = useState<string | undefined>(undefined);
  const [bannerImageSrc, setBannerImageSrc] = useState<string | undefined>(undefined);
  const [manImageSrc, setManImageSrc] = useState<string | undefined>(undefined);
  const [chartImageSrc, setChartImageSrc] = useState<string | undefined>(undefined);
  const [awardsImageSrc, setAwardsImageSrc] = useState<string | undefined>(undefined);
  const [chart1ImageUrl, setChart1ImageUrl] = useState<string | undefined>(data?.chart1ImageDataUrl);
  const [chart2ImageUrl, setChart2ImageUrl] = useState<string | undefined>(data?.chart2ImageDataUrl);
  const [chart3ImageUrl, setChart3ImageUrl] = useState<string | undefined>(data?.chart3ImageDataUrl);
  const [pieChart1ImageUrl, setPieChart1ImageUrl] = useState<string | undefined>(data?.pieChart1ImageDataUrl);
  const [pieChart2ImageUrl, setPieChart2ImageUrl] = useState<string | undefined>(data?.pieChart2ImageDataUrl);
  const [pieChart3ImageUrl, setPieChart3ImageUrl] = useState<string | undefined>(data?.pieChart3ImageDataUrl);
  const [cellDataCache, setCellDataCache] = useState<Map<string, any>>(new Map());

  // Helper function to format date to YYYY-MM-DD for until parameter
  const formatDateToUntil = useCallback((dateStr: string | undefined): string | undefined => {
    if (!dateStr) return undefined;
    // If already YYYY-MM-DD, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    // Try to parse and format
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return undefined;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Get until date from published_at
  const until = formatDateToUntil(data?.published_at);

  // Clear cache when until changes
  useEffect(() => {
    setCellDataCache(new Map());
  }, [until]);

  // Helper function to convert blob to data URL with correct MIME type
  const blobToDataUrl = useCallback(async (blob: Blob, url?: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      // Try to detect MIME type from blob first
      let mimeType = blob.type;
      
      // If blob doesn't have a type or it's application/octet-stream, try to infer from URL
      if (!mimeType || mimeType === 'application/octet-stream') {
        if (url) {
          const lowerUrl = url.toLowerCase();
          if (lowerUrl.includes('.png') || lowerUrl.includes('png')) {
            mimeType = 'image/png';
          } else if (lowerUrl.includes('.jpg') || lowerUrl.includes('.jpeg') || lowerUrl.includes('jpg') || lowerUrl.includes('jpeg')) {
            mimeType = 'image/jpeg';
          } else if (lowerUrl.includes('.gif') || lowerUrl.includes('gif')) {
            mimeType = 'image/gif';
          } else if (lowerUrl.includes('.webp') || lowerUrl.includes('webp')) {
            mimeType = 'image/webp';
          } else {
            // Default to PNG if we can't determine
            mimeType = 'image/png';
          }
        } else {
          // Default to PNG if no URL provided
          mimeType = 'image/png';
        }
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        const base64Data = result.includes(',') ? result.split(',')[1] : result;
        
        // Detect actual image type from base64 data (magic bytes)
        let detectedMimeType = mimeType;
        if (base64Data) {
          // JPEG: starts with /9j/4AAQ or FF D8 FF
          if (base64Data.startsWith('/9j/4AAQ') || base64Data.startsWith('/9j/')) {
            detectedMimeType = 'image/jpeg';
          }
          // PNG: starts with iVBORw0KGgo
          else if (base64Data.startsWith('iVBORw0KGgo')) {
            detectedMimeType = 'image/png';
          }
          // GIF: starts with R0lGODlh
          else if (base64Data.startsWith('R0lGODlh')) {
            detectedMimeType = 'image/gif';
          }
          // WebP: starts with UklGR
          else if (base64Data.startsWith('UklGR')) {
            detectedMimeType = 'image/webp';
          }
        }
        
        // If the result has the wrong MIME type, replace it with detected type
        if (result.startsWith('data:application/octet-stream')) {
          resolve(`data:${detectedMimeType};base64,${base64Data}`);
        } else if (result.startsWith('data:') && !result.startsWith(`data:${detectedMimeType}`)) {
          // Replace with correct MIME type
          resolve(`data:${detectedMimeType};base64,${base64Data}`);
        } else {
          resolve(result);
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }, []);

  // Fetch dynamic cell data using GraphQL
  const fetchCellData = useCallback(async (cell: any, rowIndex: number, colIndex: number) => {
    if (!isin || !cell?.isDynamic || !cell?.fields?.length) return null;
    
    const cellKey = `cumulative-${rowIndex}-${colIndex}-${until || ''}`;
    
    // Check cache first
    if (cellDataCache.has(cellKey)) {
      return cellDataCache.get(cellKey);
    }

    try {
      const res = await client.query({
        query: GET_AUTOMATED_COLUMNS,
        variables: { param: { data: [cell], isin, until } },
      });
      
      const fetchedValue = res.data.getAutomatedColumns?.[0]?.[0];
      
      // Cache the result - create a new Map to trigger re-render
      setCellDataCache(prev => {
        const newCache = new Map(prev);
        newCache.set(cellKey, fetchedValue);
        return newCache;
      });
      
      return fetchedValue;
    } catch (error) {
      console.error('Failed to fetch cell data:', error);
      return null;
    }
  }, [isin, until, cellDataCache]);

  // Fetch dynamic value data using GraphQL
  const fetchDynamicValue = useCallback(async (section: string, field: string, value: any) => {
    if (!isin || !value?.isDynamic || !value?.fields?.length) return null;
    
    const valueKey = `${section}.${field}-${until || ''}`;
    
    // Check cache first
    if (cellDataCache.has(valueKey)) {
      return cellDataCache.get(valueKey);
    }

    try {
      const res = await client.query({
        query: GET_AUTOMATED_COLUMNS,
        variables: { param: { data: [value], isin, until } },
      });
      
      const fetchedValue = res.data.getAutomatedColumns?.[0]?.[0];
      
      // Cache the result
      setCellDataCache(prev => {
        const newCache = new Map(prev);
        newCache.set(valueKey, fetchedValue);
        return newCache;
      });
      
      return fetchedValue;
    } catch (error) {
      console.error('Failed to fetch dynamic value:', error);
      return null;
    }
  }, [isin, until, cellDataCache]);

  // Fetch all dynamic cells and values when dialog opens or data changes
  useEffect(() => {
    if (!open || !isin) return;
    
    const fetchAllDynamicData = async () => {
      const promises: Promise<void>[] = [];
      
      // Fetch cumulative performance table cells
      if (data?.cumulativePerformance) {
        const getCumulativePerformanceData = () => {
          const perf = data.cumulativePerformance;
          if (perf && perf.labels && Array.isArray(perf.labels) && perf.values && Array.isArray(perf.values)) {
            return {
              labels: perf.labels,
              values: perf.values,
              columns: perf.columns || [],
            };
          }
          return { labels: [], values: [], columns: [] };
        };
        
        const cumulativePerformanceData = getCumulativePerformanceData();
        
        cumulativePerformanceData.values.forEach((row: any[], rowIndex: number) => {
          row.forEach((cell: any, colIndex: number) => {
            if (cell?.isDynamic && cell?.fields?.length) {
              promises.push(
                fetchCellData(cell, rowIndex, colIndex).then(() => undefined)
              );
            }
          });
        });
      }
      
      // Fetch masterData dynamic values
      ['fondskategorie', 'auflagedatum', 'wkn', 'isin', 'erstausgabepreis', 'fondsvolumen', 
       'kursNav', 'fondswaehrung', 'fondsdomizil', 'geschaeftsjahresende', 'ertragsverwendung', 
       'rechtsform', 'risikoErtragsprofil', 'sparplanfaehig', 'mindestzeichnungbetrag'].forEach(field => {
        const value = data?.masterData?.[field];
        if (value?.isDynamic && value?.fields?.length) {
          promises.push(
            fetchDynamicValue('masterData', field, value).then(() => undefined)
          );
        }
      });
      
      // Fetch fees dynamic values
      ['ausgabeaufschlag', 'laufendeKostenTer', 'davonVerwaltungsgebuehr'].forEach(field => {
        const value = data?.fees?.[field];
        if (value?.isDynamic && value?.fields?.length) {
          promises.push(
            fetchDynamicValue('fees', field, value).then(() => undefined)
          );
        }
      });
      
      await Promise.all(promises);
    };
    
    fetchAllDynamicData();
  }, [open, isin, data?.cumulativePerformance, data?.masterData, data?.fees, fetchCellData, fetchDynamicValue, until]);

  useEffect(() => {
    setChart1ImageUrl(data?.chart1ImageDataUrl);
    setChart2ImageUrl(data?.chart2ImageDataUrl);
    setChart3ImageUrl(data?.chart3ImageDataUrl);
    setPieChart1ImageUrl(data?.pieChart1ImageDataUrl);
    setPieChart2ImageUrl(data?.pieChart2ImageDataUrl);
    setPieChart3ImageUrl(data?.pieChart3ImageDataUrl);
  }, [data?.chart1ImageDataUrl, data?.chart2ImageDataUrl, data?.chart3ImageDataUrl, data?.pieChart1ImageDataUrl, data?.pieChart2ImageDataUrl, data?.pieChart3ImageDataUrl]);

  useEffect(() => {
    let active = true;
    async function load() {
      const url = template?.icon_url as string | undefined;
      if (!url) {
        if (active) setLogoSrc(undefined);
        return;
      }

      try {
        const s3Key = url.replace('https://cixon-finance-docs.s3.amazonaws.com/', '');
        const downloadUrl = `/docs/files/download/${s3Key}`;

        const blob: Blob = await http.getBlob(downloadUrl);
        const dataUrl = await blobToDataUrl(blob, url);
        if (active) setLogoSrc(dataUrl);
      } catch (error) {
        if (active) setLogoSrc(url);
      }
    }
    load();
    return () => { active = false; };
  }, [template?.icon_url, blobToDataUrl]);

  useEffect(() => {
    let active = true;
    async function load() {
      const url = template?.defaultFields?.additional_fields?.banner_image as string | undefined;
      if (!url) {
        if (active) setBannerImageSrc(undefined);
        return;
      }

      try {
        const s3Key = url.replace('https://cixon-finance-docs.s3.amazonaws.com/', '');
        const downloadUrl = `/docs/files/download/${s3Key}`;

        const blob: Blob = await http.getBlob(downloadUrl);
        const dataUrl = await blobToDataUrl(blob, url);
        if (active) setBannerImageSrc(dataUrl);
      } catch (error) {
        if (active) setBannerImageSrc(url);
      }
    }
    load();
    return () => { active = false; };
  }, [template?.defaultFields?.additional_fields?.banner_image, blobToDataUrl]);

  // Load man image from S3 or user data
  useEffect(() => {
    let active = true;
    async function load() {
      // Check both template defaults and user-uploaded image
      const url = (data?.fundManager?.photoUrl || template?.defaultFields?.additional_fields?.man_image) as string | undefined;
      
      if (!url) {
        if (active) setManImageSrc(undefined);
        return;
      }

      // If it's already a data URL, use it directly
      if (url.startsWith('data:')) {
        if (active) setManImageSrc(url);
        return;
      }

      // Check if it's an S3 URL
      if (url.includes('cixon-finance-docs.s3.amazonaws.com')) {
        try {
          // Extract S3 key - handle both full URL and just the key
          let s3Key = '';
          
          // First, check if the URL is duplicated (contains the base URL twice)
          let cleanUrl = url;
          if (url.includes('https://cixon-finance-docs.s3.amazonaws.com/https://cixon-finance-docs.s3.amazonaws.com/')) {
            // Remove the duplicate base URL
            cleanUrl = url.replace('https://cixon-finance-docs.s3.amazonaws.com/', '');
          } else if (url.includes('http://cixon-finance-docs.s3.amazonaws.com/http://cixon-finance-docs.s3.amazonaws.com/')) {
            cleanUrl = url.replace('http://cixon-finance-docs.s3.amazonaws.com/', '');
          }
          
          // Try to extract the key using regex (most reliable)
          const s3UrlMatch = cleanUrl.match(/cixon-finance-docs\.s3\.amazonaws\.com\/([^?\s#]+)/);
          if (s3UrlMatch && s3UrlMatch[1]) {
            s3Key = s3UrlMatch[1];
          } else if (cleanUrl.startsWith('https://cixon-finance-docs.s3.amazonaws.com/')) {
            s3Key = cleanUrl.replace('https://cixon-finance-docs.s3.amazonaws.com/', '').split('?')[0].split('#')[0];
          } else if (cleanUrl.startsWith('http://cixon-finance-docs.s3.amazonaws.com/')) {
            s3Key = cleanUrl.replace('http://cixon-finance-docs.s3.amazonaws.com/', '').split('?')[0].split('#')[0];
          } else {
            // If it doesn't contain the base URL, assume it's just the key
            s3Key = cleanUrl.split('?')[0].split('#')[0];
          }
          
          // Ensure we have a valid key (not empty and not a full URL)
          if (!s3Key || s3Key.includes('://') || s3Key.includes('cixon-finance-docs.s3.amazonaws.com')) {
            console.error('Invalid S3 key extracted:', s3Key, 'from URL:', url);
            if (active) setManImageSrc(undefined);
            return;
          }
          
          const downloadUrl = `/docs/files/download/${s3Key}`;
          const blob: Blob = await http.getBlob(downloadUrl);
          const dataUrl = await blobToDataUrl(blob, url);
          if (active) setManImageSrc(dataUrl);
        } catch (error) {
          console.error('Error loading man image:', error);
          // If it's an S3 URL, we can't use it directly in PDF, so leave it undefined
          if (active) setManImageSrc(undefined);
        }
      } else {
        // For non-S3 URLs, try to fetch and convert to data URL
        try {
          const response = await fetch(url);
          const blob = await response.blob();
          const dataUrl = await blobToDataUrl(blob, url);
          if (active) setManImageSrc(dataUrl);
        } catch (error) {
          console.error('Error loading man image from external URL:', error);
          // Can't use external URLs directly in PDF due to CORS
          if (active) setManImageSrc(undefined);
        }
      }
    }
    load();
    return () => { active = false; };
  }, [data?.fundManager?.photoUrl, template?.defaultFields?.additional_fields?.man_image, blobToDataUrl]);

  // Load chart image from S3 via backend API (same approach as logo)
  useEffect(() => {
    let active = true;
    async function load() {
      const chartImageUrl = data?.chartImageUrl;
      
      if (!chartImageUrl) {
        if (active) setChartImageSrc(undefined);
        return;
      }

      if (chartImageUrl.startsWith('data:')) {
        // If it's already a data URL, use it directly
        if (active) setChartImageSrc(chartImageUrl);
        return;
      }

      try {
        // Use backend API endpoint to download the image (same as logo)
        const s3Key = chartImageUrl.replace('https://cixon-finance-docs.s3.amazonaws.com/', '');
        const downloadUrl = `/docs/files/download/${s3Key}`;
        const blob: Blob = await http.getBlob(downloadUrl);
        const dataUrl = await blobToDataUrl(blob, chartImageUrl);
        
        if (active && dataUrl) {
          setChartImageSrc(dataUrl);
        }
      } catch (error) {
        console.error('Failed to load chart image for PDF:', error);
        if (active) setChartImageSrc(undefined);
      }
    }
    load();
    return () => { active = false; };
  }, [data?.chartImageUrl, blobToDataUrl]);

  // Load awards image from S3
  useEffect(() => {
    let active = true;
    async function load() {
      const awardsImageUrl = data?.awardsImageUrl;
      if (!awardsImageUrl) {
        if (active) setAwardsImageSrc(undefined);
        return;
      }

      // Check if it's an S3 URL
      if (awardsImageUrl.includes('cixon-finance-docs.s3.amazonaws.com')) {
        try {
          // Extract S3 key - handle both full URL and just the key
          let s3Key = '';
          
          // First, check if the URL is duplicated (contains the base URL twice)
          let cleanUrl = awardsImageUrl;
          if (awardsImageUrl.includes('https://cixon-finance-docs.s3.amazonaws.com/https://cixon-finance-docs.s3.amazonaws.com/')) {
            // Remove the duplicate base URL
            cleanUrl = awardsImageUrl.replace('https://cixon-finance-docs.s3.amazonaws.com/', '');
          } else if (awardsImageUrl.includes('http://cixon-finance-docs.s3.amazonaws.com/http://cixon-finance-docs.s3.amazonaws.com/')) {
            cleanUrl = awardsImageUrl.replace('http://cixon-finance-docs.s3.amazonaws.com/', '');
          }
          
          // Try to extract the key using regex (most reliable)
          const s3UrlMatch = cleanUrl.match(/cixon-finance-docs\.s3\.amazonaws\.com\/([^/\s#]+)/);
          if (s3UrlMatch && s3UrlMatch[1]) {
            s3Key = s3UrlMatch[1];
          } else if (cleanUrl.startsWith('https://cixon-finance-docs.s3.amazonaws.com/')) {
            s3Key = cleanUrl.replace('https://cixon-finance-docs.s3.amazonaws.com/', '').split('?')[0].split('#')[0];
          } else if (cleanUrl.startsWith('http://cixon-finance-docs.s3.amazonaws.com/')) {
            s3Key = cleanUrl.replace('http://cixon-finance-docs.s3.amazonaws.com/', '').split('?')[0].split('#')[0];
          } else {
            // If it doesn't contain the base URL, assume it's just the key
            s3Key = cleanUrl.split('?')[0].split('#')[0];
          }
          
          // Ensure we have a valid key (not empty and not a full URL)
          if (!s3Key || s3Key.includes('://') || s3Key.includes('cixon-finance-docs.s3.amazonaws.com')) {
            console.error('Invalid S3 key extracted:', s3Key, 'from URL:', awardsImageUrl);
            if (active) setAwardsImageSrc(undefined);
            return;
          }
          
          const downloadUrl = `/docs/files/download/${s3Key}`;
          const blob: Blob = await http.getBlob(downloadUrl);
          const dataUrl = await blobToDataUrl(blob, awardsImageUrl);
          if (active) setAwardsImageSrc(dataUrl);
        } catch (error) {
          console.error('Error loading awards image:', error);
          // Fallback: try to use the URL directly if it's already a data URL
          if (awardsImageUrl.startsWith('data:')) {
            if (active) setAwardsImageSrc(awardsImageUrl);
          } else {
            // If it's an S3 URL, we can't use it directly in PDF, so leave it undefined
            if (active) setAwardsImageSrc(undefined);
          }
        }
      } else if (awardsImageUrl.startsWith('data:')) {
        // If it's already a data URL, use it directly
        if (active) setAwardsImageSrc(awardsImageUrl);
      } else {
        // For other URLs, try to use directly (might not work in PDF)
        if (active) setAwardsImageSrc(awardsImageUrl);
      }
    }
    load();
    return () => { active = false; };
  }, [data?.awardsImageUrl, blobToDataUrl]);

  const downloadFileName = useMemo(
    () =>
      buildFactsheetDownloadFileName({
        customTemplate: data?.pdf_download_name,
        title: data?.title || template?.fond || template?.name || 'Factsheet',
        asOf: resolveFactsheetDownloadAsOf(data) || until,
      }),
    [data, template?.fond, template?.name, until],
  );

  const factsheetPdf = (
    <FactsheetPDF
      data={data}
      logoSrc={logoSrc}
      chart1ImageUrl={chart1ImageUrl}
      chart2ImageUrl={chart2ImageUrl}
      chart3ImageUrl={chart3ImageUrl}
      pieChart1ImageUrl={pieChart1ImageUrl}
      pieChart2ImageUrl={pieChart2ImageUrl}
      pieChart3ImageUrl={pieChart3ImageUrl}
      bannerImageSrc={bannerImageSrc}
      manImageSrc={manImageSrc}
      chartImageSrc={chartImageSrc}
      awardsImageSrc={awardsImageSrc}
      isin={isin}
      cellDataCache={cellDataCache}
      until={until}
    />
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={dialogStyles.dialog}
    >
      <DialogTitle sx={dialogStyles.dialogTitle}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <Box>{title}</Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={dialogStyles.dialogContent}>
        <Box sx={dialogStyles.pdfContainer}>
          {open ? (
            <PdfPreviewWithTools document={factsheetPdf} fileName={downloadFileName} />
          ) : null}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PdfPreviewDialog;
